// THE REP'S SENT FOLDER, as a thing a row can be asked about.
//
// The server indexes the whole sent mailbox (gmail/handler.py#_sent_index);
// this wraps that payload in the one question every surface actually asks:
// "have we written to this company, and when?" — matched on the exact address
// first, then the company's own mail domain. Freemail domains never match by
// domain: half the contractors in this directory use gmail.com, and one send
// to a gmail address must not mark every other gmail lead as contacted.
import { gmailSentIndex } from './gmail.js'
import { normDomain } from './hubspot.js'

const FREEMAIL = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'hotmail.com', 'outlook.com',
  'live.com', 'msn.com', 'aol.com', 'icloud.com', 'me.com', 'mac.com', 'comcast.net',
  'att.net', 'sbcglobal.net', 'verizon.net', 'bellsouth.net', 'cox.net', 'charter.net',
  'earthlink.net', 'protonmail.com', 'proton.me', 'mail.com',
])
const ADDR = /[\w.+-]+@[\w-]+\.[\w.-]+/g

/** Every address a row answers to: its own, plus any in the packed contacts. */
export function rowAddresses(row) {
  const out = new Set()
  const own = String(row?.email || '').trim().toLowerCase()
  if (own) out.add(own)
  for (const f of ['contacts', 'contacts_raw', 'contactsRaw']) {
    for (const m of String(row?.[f] || '').match(ADDR) || []) out.add(m.toLowerCase())
  }
  return out
}

/**
 * Wrap a sent-index payload. Returns null when the lane could not be read, so
 * a caller can say "unknown" instead of "nobody was emailed".
 */
export function sentMailIndex(payload) {
  if (!payload || payload.checked === false) return null
  const recipients = payload.recipients || {}
  const byAddress = new Map()
  const byDomain = new Map()
  for (const [addr, meta] of Object.entries(recipients)) {
    const a = String(addr).toLowerCase()
    const n = Number(meta?.n) || 1
    const last = String(meta?.last || '')
    byAddress.set(a, { n, last })
    const dom = a.split('@')[1] || ''
    if (!dom || FREEMAIL.has(dom)) continue
    const cur = byDomain.get(dom)
    if (!cur) byDomain.set(dom, { n, last })
    else {
      cur.n += n
      if (last > cur.last) cur.last = last
    }
  }
  return {
    address: String(payload.address || ''),
    messagesRead: Number(payload.messagesRead) || 0,
    truncated: Boolean(payload.truncated),
    recipients: byAddress.size,
    /** { n, last, how } for a row we have written to, else null. */
    matchRow(row) {
      for (const a of rowAddresses(row)) {
        const hit = byAddress.get(a)
        if (hit) return { ...hit, how: 'address' }
      }
      const dom = normDomain(row?.website)
      if (dom && !FREEMAIL.has(dom)) {
        const hit = byDomain.get(dom)
        if (hit) return { ...hit, how: 'domain' }
      }
      return null
    },
  }
}

/** Fetch + wrap. Never throws; null means the lane is unread. */
export async function loadSentMail() {
  try {
    return sentMailIndex(await gmailSentIndex())
  } catch {
    return null
  }
}
